<template>
  <div>
    <div v-if="!jobTitleId" class="row mb-4">
      <div class="col-md-4">
        <label class="label-input font-weight-bold">Chức vụ:</label>
        <AutoSuggest
          :model="searchText"
          :suggestions="filteredJobTitles"
          placeholder="chức vụ"
          suggestion-name="name"
          :show-clear="!!selectedJobTitleId"
          @change="onSearchChange"
          @select="onSelectJobTitle"
        />
      </div>
    </div>

    <div v-if="selectedJobTitleId === null" class="text-center text-muted py-5">
      <i class="flaticon2-information" style="font-size: 3rem; opacity: 0.3"></i>
      <p class="mt-3">Vui lòng chọn chức vụ để xem cấu hình giới hạn ngày</p>
    </div>

    <div v-else>
      <vcl-table v-if="loading" :speed="5" :animate="true" :columns="3" />
      <div v-else>
        <DayLimitMatrix v-model="dayLimitConfigs" />
        <div class="mt-3 d-flex justify-content-end">
          <b-button
            variant="primary"
            size="sm"
            :disabled="saving"
            @click="showConfirmModal = true"
          >
            <b-spinner v-if="saving" small class="mr-1" />
            Lưu cấu hình
          </b-button>
        </div>
      </div>
    </div>

    <b-modal
      v-model="showConfirmModal"
      title="Áp dụng cho tất cả tài khoản?"
      no-close-on-backdrop
      centered
    >
      <p>
        Bạn có muốn đặt lại giới hạn ngày cho toàn bộ tài khoản thuộc chức vụ
        <strong>{{ selectedJobTitleName }}</strong> theo cấu hình này không?
      </p>
      <p class="text-warning mb-0">
        <i class="fas fa-exclamation-triangle mr-1"></i>
        Các cài đặt riêng của từng tài khoản sẽ bị xóa và kế thừa lại từ chức vụ.
      </p>
      <template #modal-footer>
        <b-button variant="secondary" size="sm" @click="showConfirmModal = false">
          Hủy
        </b-button>
        <b-button variant="outline-primary" size="sm" @click="save(false)">
          Chỉ lưu chức vụ
        </b-button>
        <b-button variant="primary" size="sm" @click="save(true)">
          Áp dụng tất cả
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { VclTable } from 'vue-content-loading';
import { handleError, makeToastSuccess } from '@/utils/common';
import DayLimitMatrix from '@/view/components/DayLimitMatrix.vue';
import AutoSuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';

export default {
  name: 'DayLimitJobTitleConfig',
  components: { VclTable, DayLimitMatrix, AutoSuggest },
  props: {
    jobTitleId: {
      type: Number,
      default: null,
    },
    jobTitleName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      allJobTitles: [],
      filteredJobTitles: [],
      searchText: '',
      selectedJobTitleId: null,
      selectedJobTitleName: '',
      dayLimitConfigs: {},
      loading: false,
      saving: false,
      showConfirmModal: false,
    };
  },
  created() {
    if (this.jobTitleId) {
      this.selectedJobTitleId = this.jobTitleId;
      this.selectedJobTitleName = this.jobTitleName;
      this.fetchDayLimitConfigs(this.jobTitleId);
    } else {
      this.fetchJobTitles();
    }
  },
  methods: {
    async fetchJobTitles() {
      try {
        const { data } = await ApiService.get('v1/job-titles');
        this.allJobTitles = (data.data || []).map(jt => ({ id: jt.id, name: jt.name }));
        this.filteredJobTitles = this.allJobTitles;
      } catch (error) {
        handleError(error);
      }
    },
    onSearchChange(text) {
      this.searchText = text || '';
      if (!text) {
        this.selectedJobTitleId = null;
        this.selectedJobTitleName = '';
        this.dayLimitConfigs = {};
      }
      const keyword = (text || '').toLowerCase().trim();
      this.filteredJobTitles = keyword
        ? this.allJobTitles.filter(jt => jt.name.toLowerCase().includes(keyword))
        : this.allJobTitles;
    },
    onSelectJobTitle(option) {
      if (!option) return;
      const jt = option.item;
      this.searchText = jt.name;
      this.selectedJobTitleId = jt.id;
      this.selectedJobTitleName = jt.name;
      this.fetchDayLimitConfigs(jt.id);
    },
    async fetchDayLimitConfigs(jobTitleId) {
      this.loading = true;
      this.dayLimitConfigs = {};
      try {
        const { data } = await ApiService.get(`v1/day-limit-configs/job-titles/${jobTitleId}`);
        this.dayLimitConfigs = this.parseConfigsToMap(data.data || []);
      } catch (error) {
        handleError(error);
      } finally {
        this.loading = false;
      }
    },
    parseConfigsToMap(configs) {
      return configs.reduce((acc, item) => {
        acc[item.featureKey] = { viewDays: item.viewDays, editDays: item.editDays };
        return acc;
      }, {});
    },
    async save(resetUserConfigs) {
      this.showConfirmModal = false;
      this.saving = true;
      try {
        const configs = Object.entries(this.dayLimitConfigs).map(([featureKey, val]) => ({
          featureKey,
          viewDays: val.viewDays ?? null,
          editDays: val.editDays ?? null,
        }));
        await ApiService.put(`v1/day-limit-configs/job-titles/${this.selectedJobTitleId}`, {
          configs,
          resetUserConfigs,
        });
        makeToastSuccess('Lưu cấu hình giới hạn ngày thành công!');
      } catch (error) {
        handleError(error);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
